import { useEffect, useState } from "react";
import { Breadcrumbs, P } from "AbstractElements";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  Col,
  Card,
  CardBody,
  Spinner,
} from "reactstrap";
import {
  startAuditThunk,
  getAuditResultsThunk,
  solveResultThunk,
} from "Features/Learning/Tryout/aiAuditSlice";
import TryoutAIAuditHeader from "./components/TryoutAIAuditHeader";
import TryoutAIAuditStats from "./components/TryoutAIAuditStats";
import TryoutAIAuditTable from "./components/TryoutAIAuditTable";
import { StartAuditModal, SolveConfirmModal, ReauditConfirmModal } from "./components/TryoutAIAuditModals";
import AuditProcessingModal from "./components/AuditProcessingModal";

const TryoutAIAudit = () => {
  const { id } = useParams();
  const dispatch = useDispatch();

  const {
    status,
    results,
    summary,
    errorStage,
    errorMessage,
    statistics,
    verifiedByAI,
    isLoading,
    isStarting,
    error,
    title, // tryout title dari response GET
  } = useSelector((state) => state.aiAudit);

  // State modal konfirmasi sebelum memulai audit AI.
  const [isModalOpen, setIsModalOpen] = useState(false);
  // State baris accordion yang terbuka (hanya satu di satu waktu).
  const [openId, setOpenId] = useState(null);
  // State modal konfirmasi aksi solve/unsolve.
  const [solveConfirm, setSolveConfirm] = useState({ item: null, isSolving: false });
  // Per-row loading state (Set of item.id yang sedang diproses).
  const [solvingIds, setSolvingIds] = useState(new Set());

  // State modal konfirmasi re-audit (hapus hasil lama).
  const [isReauditModalOpen, setIsReauditModalOpen] = useState(false);
  // State modal informasi proses audit sedang berjalan.
  const [isProcessingModalOpen, setIsProcessingModalOpen] = useState(false);

  useEffect(() => {
    if (!id) return;
    dispatch(getAuditResultsThunk(id));
  }, [id, dispatch]);

  useEffect(() => {
    if (status === "auditing") {
      setIsProcessingModalOpen(true);
      return;
    }

    if (status === "done" || status === "error" || status === "not_audited" || status === "idle") {
      setIsProcessingModalOpen(false);
    }
  }, [status]);

  // Buka modal konfirmasi (belum dispatch).
  const handleOpenModal = () => {
    if (!id || isStarting) return;
    setIsModalOpen(true);
  };

  // Tutup modal konfirmasi.
  const handleCloseModal = () => {
    if (isStarting) return; // jangan tutup saat sedang proses
    setIsModalOpen(false);
  };

  // Konfirmasi dari modal: dispatch startAudit lalu tutup modal.
  const handleConfirmStart = () => {
    if (!id || isStarting) return;
    dispatch(startAuditThunk({ tryoutId: id, confirmReaudit: false }));
    setIsModalOpen(false);
    setIsProcessingModalOpen(true);
  };

  // Buka modal konfirmasi solve/unsolve.
  const handleOpenSolveConfirm = (item, isSolving) => {
    setSolveConfirm({ item, isSolving });
  };

  // Tutup modal konfirmasi solve/unsolve.
  const handleCloseSolveConfirm = () => {
    setSolveConfirm({ item: null, isSolving: false });
  };

  // Buka modal konfirmasi re-audit.
  const handleOpenReaudit = () => {
    if (!id || isStarting) return;
    setIsReauditModalOpen(true);
  };

  // Tutup modal konfirmasi re-audit.
  const handleCloseReaudit = () => {
    setIsReauditModalOpen(false);
  };

  // Konfirmasi re-audit: dispatch startAudit dengan confirm_reaudit: true.
  const handleConfirmReaudit = () => {
    if (!id || isStarting) return;
    dispatch(startAuditThunk({ tryoutId: id, confirmReaudit: true }));
    setIsReauditModalOpen(false);
  };

  // Eksekusi solve/unsolve setelah dikonfirmasi.
  const handleConfirmSolve = () => {
    const { item, isSolving } = solveConfirm;
    if (!item) return;
    // Tambahkan ke solvingIds untuk loading state.
    setSolvingIds((prev) => new Set(prev).add(item.id));
    dispatch(
      solveResultThunk({ id: item.id, isSolved: isSolving })
    ).then(() => {
      // Hapus dari solvingIds setelah selesai (fulfilled atau rejected).
      setSolvingIds((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
      handleCloseSolveConfirm();
    });
  };

  const resultsList = Array.isArray(results) ? results : [];
  const resultsCount = resultsList.length;

  // Toggle baris accordion (hanya satu terbuka).
  const handleToggleRow = (rowId) => {
    setOpenId((prev) => (prev === rowId ? null : rowId));
  };

  return (
    <>
      <Breadcrumbs
        mainTitle="Audit AI Tryout"
        parent="Learning"
        title="Audit AI Tryout"
      />
      <Col lg="12">
        <Card
          className="border-0 shadow-sm overflow-hidden"
          style={{
            borderRadius: "1.5rem",
            background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
          }}
        >
          <CardBody className="p-3 p-md-4">
            <TryoutAIAuditHeader
              title={title}
              verifiedByAI={verifiedByAI}
              id={id}
              isStarting={isStarting}
              status={status}
              isLoading={isLoading}
              onStartClick={status === "done" || status === "error" ? handleOpenReaudit : handleOpenModal}
            />

            {/* Loading saat mengambil hasil */}
            {isLoading && (
              <div className="loader-box">
                <Spinner attrSpinner={{ className: "loader-30" }} />
              </div>
            )}

            {/* Error generik (network / reject) */}
            {error && (
              <div className="rounded-4 border border-danger-subtle bg-danger-subtle p-3 mb-3">
                <P className="mb-1 fw-semibold text-danger">Terjadi kesalahan</P>
                <P className="mb-0 text-danger-emphasis">
                  {typeof error === "string" ? error : "Silakan coba lagi sebentar."}
                </P>
              </div>
            )}

            {/* Error dari proses audit (verified_by_AI === 'failed') */}
            {!isLoading &&
              status === "error" &&
              verifiedByAI === "failed" && (
                <div className="alert alert-danger">
                  <P className="mb-1">
                    <strong>Audit gagal:</strong>{" "}
                    {errorStage || "Tahap tidak diketahui"}
                  </P>
                  {errorMessage && <P className="mb-0">{errorMessage}</P>}
                </div>
              )}

            {/* 202 diterima: audit sedang berjalan */}
            {!isLoading && status === "auditing" && (
              <>
                <div className="rounded-4 border border-primary-subtle bg-primary-subtle p-3 mb-3">
                  <P className="mb-1 fw-semibold text-primary">Proses Audit Sedang Berjalan</P>
                  <P className="mb-0 text-muted">
                    Audit sudah masuk antrean dan sedang diproses oleh sistem. Hasil akan muncul secara otomatis setelah selesai.
                  </P>
                </div>
                {statistics && (
                  <P className="text-muted">
                    Soal diaudit: {statistics.total_audited ?? 0}/
                    {statistics.total_questions ?? 0}
                  </P>
                )}
              </>
            )}

            {/* Belum pernah diaudit */}
            {!isLoading && status === "not_audited" && (
              <div className="rounded-4 border border-secondary-subtle bg-white p-3 mb-3">
                <P className="mb-1 fw-semibold text-black">Belum ada audit yang dijalankan</P>
                <P className="mb-0 text-black">
                  Klik tombol <strong>Mulai Audit AI</strong> untuk memeriksa seluruh soal tryout secara menyeluruh.
                </P>
              </div>
            )}

            {/* Ringkasan AI */}
            {!isLoading && summary && (
              <div className="rounded-4 border border-primary-subtle bg-white p-3 mb-3">
                <P className="mb-1 fw-semibold text-black">Ringkasan AI</P>
                <P className="mb-0 text-black">{summary}</P>
              </div>
            )}

            {/* Kartu statistik */}
            {!isLoading && statistics && <TryoutAIAuditStats statistics={statistics} />}

            {/* Empty state */}
            {!isLoading &&
              (status === "done" || status === "not_audited") &&
              resultsList.length === 0 && (
                <div className="rounded-4 border border-dashed bg-light p-4 text-center text-muted">
                  Belum ada temuan audit untuk tryout ini.
                </div>
              )}

            {/* Daftar hasil audit (Table dengan baris collapsible) */}
            {!isLoading && resultsCount > 0 && (
              <>
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-2 mb-3">
                  <div>
                    <h5 className="mb-1 fw-semibold">Daftar Hasil Audit</h5>
                    <small className="text-muted">
                      Ditemukan {resultsCount} temuan. Klik setiap baris untuk melihat detail lengkap.
                    </small>
                  </div>
                </div>
                <TryoutAIAuditTable
                  resultsList={resultsList}
                  openId={openId}
                  onToggleRow={handleToggleRow}
                  onSolveClick={handleOpenSolveConfirm}
                  solvingIds={solvingIds}
                />
              </>
            )}
          </CardBody>
        </Card>
      </Col>

      {/* Modal konfirmasi mulai audit AI */}
      <StartAuditModal
        isOpen={isModalOpen}
        isStarting={isStarting}
        onCancel={handleCloseModal}
        onConfirm={handleConfirmStart}
      />

      {/* Modal konfirmasi Selesaikan/Buka Kembali */}
      <SolveConfirmModal
        solveConfirm={solveConfirm}
        solvingIds={solvingIds}
        onCancel={handleCloseSolveConfirm}
        onConfirm={handleConfirmSolve}
      />

      {/* Modal konfirmasi re-audit (hapus hasil lama) */}
      <ReauditConfirmModal
        isOpen={isReauditModalOpen}
        onCancel={handleCloseReaudit}
        onConfirm={handleConfirmReaudit}
      />

      <AuditProcessingModal
        isOpen={isProcessingModalOpen}
        onClose={() => setIsProcessingModalOpen(false)}
      />
    </>

  );
};

export default TryoutAIAudit;